/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../Modal/Modal';
import useGoogleMaps from '../../hooks/useGoogleMaps';
import { GhostButton, PrimaryButton } from '../../styles/ui/Button';

// ===========================================================================
// LocationPickModal — seletor de ponto CONTROLADO e COMPARTILHADO.
//
// Não persiste nada: geocodifica o endereço recebido (sugestão), deixa o
// operador confirmar/ajustar clicando ou arrastando o pino, e devolve
// { latitude, longitude, place_id } por callback. Reusa o loader
// `useGoogleMaps`. Sem chave do Maps, cai no fallback de dois campos lat/long.
//
// place_id: vem do resultado do geocode (âncora durável). Ao clicar/arrastar
// (pin manual puro) o place_id é zerado — coerente com o backend (pin novo sem
// place_id zera a âncora antiga).
//
// Consumidores: AddressFormModal (Comercial + Roteirização, via customer_address).
// ===========================================================================

const FALLBACK_CENTER = { lat: -16.6869, lng: -49.2648 }; // Goiânia

const isValidLat = (n) => Number.isFinite(n) && n >= -90 && n <= 90;
const isValidLng = (n) => Number.isFinite(n) && n >= -180 && n <= 180;

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
`;

const MapBox = styled.div`
   width: 100%;
   height: 340px;
   border-radius: 12px;
   overflow: hidden;
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.12)'};
   background: #eef1f5;
`;

const SearchRow = styled.div`
   display: flex;
   gap: 8px;

   input {
      flex: 1;
      height: 40px;
      padding: 0 11px;
      border-radius: 11px;
      border: 1px solid #dde2e9;
      font-size: 14px;
      font-family: inherit;
   }
   input:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      box-shadow: 0 0 0 3px rgba(24, 95, 165, 0.16);
   }
`;

const CoordLine = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 12.5px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};

   .mono {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
`;

const FallbackGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;

   label {
      display: block;
      font-size: 11.5px;
      font-weight: 600;
      margin-bottom: 4px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   input {
      width: 100%;
      height: 40px;
      padding: 0 11px;
      border-radius: 11px;
      border: 1px solid #dde2e9;
      font-size: 14px;
      font-family: inherit;
   }
   input:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      box-shadow: 0 0 0 3px rgba(24, 95, 165, 0.16);
   }
`;

const Notice = styled.div`
   font-size: 12.5px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

export default function LocationPickModal({
   isOpen,
   onClose,
   onConfirm,
   initialAddress = '',
   initialCoord = null,
}) {
   const { apiKey, isLoaded, loadError } = useGoogleMaps();
   const useMap = Boolean(apiKey && isLoaded && !loadError);
   const noMap = !apiKey || Boolean(loadError);
   const mapLoading = Boolean(apiKey) && !isLoaded && !loadError;

   const [coord, setCoord] = useState(initialCoord);
   const [placeId, setPlaceId] = useState(null);
   const [latInput, setLatInput] = useState(
      initialCoord ? String(initialCoord.lat) : '',
   );
   const [lngInput, setLngInput] = useState(
      initialCoord ? String(initialCoord.lng) : '',
   );
   const [searchInput, setSearchInput] = useState(initialAddress);
   const [searchStatus, setSearchStatus] = useState(null); // null|'searching'|'notfound'

   const containerRef = useRef(null);
   const mapRef = useRef(null);
   const markerRef = useRef(null);
   const geocoderRef = useRef(null);
   const aliveRef = useRef(true);
   useEffect(() => {
      aliveRef.current = true;
      return () => {
         aliveRef.current = false;
      };
   }, []);

   // placeIdArg: âncora durável do geocode; null quando o ponto vem de clique/
   // arraste (pin manual puro).
   const applyPoint = useCallback((lat, lng, placeIdArg = null) => {
      setCoord({ lat, lng });
      setLatInput(String(lat));
      setLngInput(String(lng));
      setPlaceId(placeIdArg);
   }, []);

   const runGeocode = useCallback(
      (query, { pan = true } = {}) => {
         const geocoder = geocoderRef.current;
         const trimmed = (query || '').trim();
         if (!geocoder || !trimmed) return;
         setSearchStatus('searching');
         geocoder.geocode(
            { address: trimmed, componentRestrictions: { country: 'BR' } },
            (results, status) => {
               if (!aliveRef.current) return;
               if (status === 'OK' && results && results[0]) {
                  const loc = results[0].geometry.location;
                  applyPoint(loc.lat(), loc.lng(), results[0].place_id ?? null);
                  setSearchStatus(null);
                  if (pan && mapRef.current) {
                     mapRef.current.panTo(loc);
                     mapRef.current.setZoom(16);
                  }
               } else {
                  setSearchStatus('notfound');
               }
            },
         );
      },
      [applyPoint],
   );

   // Inicializa o mapa quando a API está pronta e o container montado.
   useEffect(() => {
      if (!useMap) return;
      const g = window.google;
      if (!g || !containerRef.current || mapRef.current) return;

      geocoderRef.current = new g.maps.Geocoder();
      mapRef.current = new g.maps.Map(containerRef.current, {
         center: initialCoord || FALLBACK_CENTER,
         zoom: initialCoord ? 16 : 12,
         mapTypeControl: false,
         streetViewControl: false,
         fullscreenControl: false,
         clickableIcons: false,
         keyboardShortcuts: false,
         gestureHandling: 'greedy',
         styles: [
            { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
         ],
      });
      mapRef.current.addListener('click', (e) => {
         applyPoint(e.latLng.lat(), e.latLng.lng());
      });

      // Sem ponto inicial mas com endereço → sugere geocodificando.
      if (!initialCoord && initialAddress.trim()) {
         runGeocode(initialAddress, { pan: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [useMap]);

   // Sincroniza o marcador arrastável com o ponto atual.
   useEffect(() => {
      if (!useMap || !mapRef.current || !coord) return;
      const g = window.google;
      if (!markerRef.current) {
         markerRef.current = new g.maps.Marker({
            position: coord,
            map: mapRef.current,
            draggable: true,
         });
         markerRef.current.addListener('dragend', (e) => {
            applyPoint(e.latLng.lat(), e.latLng.lng());
         });
      } else {
         markerRef.current.setPosition(coord);
      }
   }, [coord, useMap, applyPoint]);

   const handleLatInput = (value) => {
      setLatInput(value);
      const lat = Number(value);
      const lng = Number(lngInput);
      if (value.trim() !== '' && isValidLat(lat) && isValidLng(lng)) {
         setCoord({ lat, lng });
         setPlaceId(null);
      }
   };
   const handleLngInput = (value) => {
      setLngInput(value);
      const lng = Number(value);
      const lat = Number(latInput);
      if (value.trim() !== '' && isValidLat(lat) && isValidLng(lng)) {
         setCoord({ lat, lng });
         setPlaceId(null);
      }
   };

   const handleSearchSubmit = (e) => {
      e.preventDefault();
      runGeocode(searchInput, { pan: true });
   };

   const handleConfirm = () => {
      if (!coord) return;
      onConfirm?.({
         latitude: coord.lat,
         longitude: coord.lng,
         place_id: placeId,
      });
      onClose?.();
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose}>
            Cancelar
         </GhostButton>
         <PrimaryButton type="button" onClick={handleConfirm} disabled={!coord}>
            Usar este ponto
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Definir localização"
         maxWidth="640px"
         footer={footer}
      >
         <Body>
            {useMap && (
               <>
                  <form onSubmit={handleSearchSubmit}>
                     <SearchRow>
                        <input
                           value={searchInput}
                           onChange={(e) => setSearchInput(e.target.value)}
                           placeholder="Buscar endereço para posicionar o pino…"
                        />
                        <GhostButton
                           type="submit"
                           style={{ height: 40, padding: '0 14px' }}
                        >
                           Buscar
                        </GhostButton>
                     </SearchRow>
                  </form>
                  {searchStatus === 'searching' && (
                     <Notice>Buscando endereço…</Notice>
                  )}
                  {searchStatus === 'notfound' && (
                     <Notice>
                        Endereço não encontrado. Ajuste o texto ou clique no mapa.
                     </Notice>
                  )}
                  <MapBox ref={containerRef} />
                  <CoordLine>
                     {coord ? (
                        <>
                           Ponto:{' '}
                           <span className="mono">
                              {coord.lat.toFixed(5)}, {coord.lng.toFixed(5)}
                           </span>{' '}
                           — clique ou arraste o pino para ajustar.
                        </>
                     ) : (
                        'Clique no mapa ou busque um endereço para fixar o ponto.'
                     )}
                  </CoordLine>
               </>
            )}

            {mapLoading && <Notice>Carregando o mapa…</Notice>}

            {noMap && (
               <>
                  <Notice>
                     Mapa indisponível. Informe a coordenada manualmente
                     (latitude e longitude).
                  </Notice>
                  <FallbackGrid>
                     <div>
                        <label htmlFor="pick-lat">Latitude</label>
                        <input
                           id="pick-lat"
                           type="text"
                           inputMode="decimal"
                           value={latInput}
                           onChange={(e) => handleLatInput(e.target.value)}
                           placeholder="-16.6869"
                        />
                     </div>
                     <div>
                        <label htmlFor="pick-lng">Longitude</label>
                        <input
                           id="pick-lng"
                           type="text"
                           inputMode="decimal"
                           value={lngInput}
                           onChange={(e) => handleLngInput(e.target.value)}
                           placeholder="-49.2648"
                        />
                     </div>
                  </FallbackGrid>
               </>
            )}
         </Body>
      </Modal>
   );
}

LocationPickModal.propTypes = {
   isOpen: PropTypes.bool,
   onClose: PropTypes.func,
   onConfirm: PropTypes.func,
   initialAddress: PropTypes.string,
   initialCoord: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
   }),
};
